
import { Project } from "@/model/Project";
import { defineComponent, PropType } from "vue";
import Tier from "../model/Tiers";
import { Tier as TierInterface } from "../model/Project";
import Slider from "./Slider.vue";
import MilestoneSlider from "./MilestoneSlider.vue";
import ProjectRaise from "@/web3/projectRaise";
import { BigNumber } from "@ethersproject/bignumber";

export default defineComponent({
  name: "ProjectPreview",
  props: {
    value: Object as PropType<Project>,
    tier: Object,
  },
  data: function () {
    return {
      error: "",
      cancelVote: false,
      votes: 0,
      customFunding: 10,
      withdrawableFunds: "UNSET",
    };
  },
  watch: {
    network() {
      this.updateProject();
    },
    account() {
      this.updateProject();
    },
  },
  mounted: function () {
    this.updateProject();
  },
  components: { Slider, MilestoneSlider },
  computed: {
    network: function () {
      return this.$store.state.network;
    },
    account: function () {
      return this.$store.state.account;
    },
    votesToCancel: function () {
      const totalFunding = this.value.totalFunding;
      return this.displayDecimal(Math.ceil(totalFunding) / 2 + 1);
    },
    totalFunding: function () {
      return this.displayDecimal(this.value.totalFunding);
    },
    fundingGoal: function () {
      return this.displayDecimal(this.value.fundingGoal);
    },
    started: function () {
      return this.value?.status == 0;
    },
    funded: function () {
      return this.value?.status == 1;
    },
    finished: function () {
      return this.value?.status == 2;
    },
    cancelled: function () {
      return this.value?.status == 3;
    },
    isCreator: function () {
      if (this.$store.state.account)
        return (
          this.$store.state.account.toLowerCase() ===
          this.value.creator.toLowerCase()
        );
      else return false;
    },
  },
  methods: {
    displayDecimal(val: number) {
      const decimals = this.$store.state.network.ustDecimals;
      const div = Math.pow(10, decimals);
      return Math.floor(val / div);
    },
    update: async function () {
      await ProjectRaise.checkFundingSuccess(this.value.address);
      this.updateProject();
    },
    milestoneCheck: async function () {
      await ProjectRaise.milestoneCheck(this.value.address);
      this.updateProject();
    },
    cancelProject: async function () {
      await ProjectRaise.cancelProject(this.value.address);
      this.updateProject();
    },
    withdrawInvestment: async function () {
      try {
        await ProjectRaise.withdrawRefund(this.value.address);
        window.location.reload();
      } catch (e) {
        console.error(e);
        this.error = e.toString();
      }
    },
    async getWithdrawAbleFunds() {
      let withdrawableFunds = "UNDEFINED";
      try {
        withdrawableFunds = await ProjectRaise.getWithdrawAbleFundAmount(
          this.value.address
        );
      } catch (e) {
        console.error(e);
        this.error = e.toString();
      }

      return withdrawableFunds;
    },
    withdrawFunds: async function () {
      try {
        await ProjectRaise.withdrawFunds(this.value.address);
      } catch (e) {
        console.error(e);
        this.error = e.toString();
      }
    },
    updateProject: async function () {
      if (this.$store.state.network) {
        this.cancelVote = await ProjectRaise.getCancelVote(
          this.value.address,
          this.$store.state.account
        );

        this.withdrawableFunds = await this.getWithdrawAbleFunds();
        this.votes = await ProjectRaise.getVotesCount(this.value.address);
      }
    },
    getTierImage: function (index: number) {
      if (this.value) {
        let img = Tier.getImage(index, this.value.tiers.length);

        return `/tiers/${img}`;
      } else return "";
    },
    getTierName: function (index: number) {
      if (this.value) {
        return Tier.getName(index, this.value.tiers.length);
      }
    },
    getSliderFill(tier: TierInterface) {
      return `width: ${((tier.backers / tier.maxBackers) * 100).toFixed(2)}%`;
    },
    calculateCurrentFunding() {
      if (this.value) {
        return this.value.tiers.reduce(
          (acc, current) => (acc += current.backers * current.cost),
          0
        );
      } else return 0;
    },
    async fundTier(tier) {
      tier.pending = true;
      await this.fund(tier.cost);
      tier.pending = false;
    },
    customFund: async function () {
      const decimals = this.$store.state.network.ustDecimals;
      this.fund(
        BigNumber.from(this.customFunding).mul(String(Math.pow(10, decimals)))
      );
    },
    fund: async function (amount) {
      try {
        await ProjectRaise.allow(
          this.$store.state.account,
          this.value.address,
          amount
        );

        await ProjectRaise.acceptBacker(this.value.address, amount);

        this.updateProject();
      } catch (e) {
        this.error = e.message.toString();
      }
    },
    voteNo: async function () {
      this.vote(true);
    },
    voteYes: async function () {
      this.vote(false);
    },
    vote: async function (cancel) {
      try {
        await ProjectRaise.vote(this.value.address, cancel);
        this.updateProject();
      } catch (e) {
        console.error(e);
        this.error = e.toString();
      }
    },
  },
});
